import { buildPrompt } from '@/ai/utils/promptBuilder'
import { readModelNotes } from '@/ai/prompts/common/instructions'
import { systemMessages } from '@/ai/prompts/common/systemMessages'
import { examples, getEventExample } from '@/ai/prompts/common/examples'
import { createSystemMessage, createUserMessage } from '@/ai/utils/promptUtils'
import { buildWorkflowSpec, buildCardTypesMap } from '@/shared/utils/workflowSpec'
import { wrapUserInput } from '@/ai/utils/helpers'
import { getJsonArrayOutputFormatting, getJsonOutputFormatting } from '@/ai/prompts/common/formatting'

export const buildWorkflowReadModelsPrompt = ({ workflow, cardTypes, stream = true }) => {
  const messages = []

  const systemContent = buildPrompt([systemMessages.EVENT_MODELING_DDD_EXPERT])
  messages.push(createSystemMessage(systemContent))

  const promptSections = []

  promptSections.push({
    title: 'Request',
    content: 'For each domain event, generate a Read Model (or Query Model) for the actor (human or system) who needs to make the necessary decisions to take action and complete the step. The purpose of the Read Model (or Query Model) is to define a query (for one or many items) that displays the current system state relevant to the actor BEFORE invoking the command. The Read Model provides background information necessary for the actor to make decisions, fill in the input form, and submit it. Usually, between 2 and 8 fields are sufficient, but it can be more or less. Generated fields should match the fields of the queried entities and include the id field of the entity. The queried entities are sometimes the same as the entities being mutated, sometimes different, sometimes belonging to different bounded contexts. An attribute can be a primitive or a non-primitive type like an associated entity with a nested structure for displaying data from a related entity or value object -- a field named as the related entity in camelCase (e.g., cartItems, shippingAddress) with sub-fields for the attributes to display. Tag each data field with indexed: true if it should be indexed for filtering or search. Field and attribute names must be in camelCase (e.g. \'productName\' not \'Product Name\') and at most 30 characters long. For each Read Model, provide the name of the entity that is the starting point for building the query (queriedEntity). The queriedEntity SHOULD preferably be one of the entity names from the workflow specification. For the top level (or starting point) root entity, as well as for each non-primitive attribute, determine if the query should return a single item or a list: use "one-to-one" if the query returns a single item (e.g., viewing details of a specific order or customer), or "one-to-many" if it returns a list of items (e.g., listing available products or order history).'
  })

  promptSections.push({
    title: 'Workflow Description',
    flattenLines: false,
    content: wrapUserInput(workflow.aiDescription)
  })

  const cardTypesMap = buildCardTypesMap(cardTypes)

  promptSections.push({
    title: 'Current Workflow Specification (JSON format)',
    contentType: 'codeblock',
    language: 'json',
    content: buildWorkflowSpec(workflow, null, cardTypesMap) || '{}'
  })

  promptSections.push({
    title: 'Very important considerations',
    contentType: 'bullet',
    content: readModelNotes
  })

  const formattingContent = `The resulting array must contain ${workflow.domainEvents.length} items.`

  const options = {
    shouldAddCards: false,
    shouldAddSchemas: true
  }

  const exampleData1 = {
    name: 'Order Placed',
    readModelName: 'Get Order Details',
    readModelExample: examples.readModelExampleOrder,
    readModelDescription: 'Orders ready for fulfillment, filterable by customer.',
    queriedEntity: 'Order',
    cardinality: 'one-to-many'
  }
  const exampleData2 = {
    name: 'Item Added to Cart',
    readModelName: 'Get Cart Details',
    readModelExample: examples.readModelExampleCart,
    queriedEntity: 'Cart',
    cardinality: 'one-to-one'
  }

  const eventsExample = [
    getEventExample(exampleData1, cardTypes, options),
    getEventExample(exampleData2, cardTypes, options)
  ]

  const eventsObject = { events: eventsExample }

  promptSections.push({
    title: 'Formatting',
    flattenLines: false,
    content: stream
      ? getJsonArrayOutputFormatting(JSON.stringify(eventsExample), formattingContent)
      : getJsonOutputFormatting('events', JSON.stringify(eventsObject), formattingContent)
  })

  const userContent = buildPrompt(promptSections)
  messages.push(createUserMessage(userContent))

  return { messages }
}
